<template>
  <v-card v-if="isAdministrator && isIRacingEvent" outlined class="mb-4">
    <v-card-title class="py-2" style="cursor: pointer;" @click="expanded = !expanded">
      <v-icon left color="primary">mdi-cloud-download</v-icon>
      <span>Administrare import iRacing</span>
      <v-spacer></v-spacer>
      <span v-if="sessionsNeedingImport.length" class="caption orange--text text--darken-2 mr-2">{{sessionsNeedingImport.length}} de importat</span>
      <v-icon>{{expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
    </v-card-title>
    <v-expand-transition>
      <div v-show="expanded">
        <v-divider></v-divider>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="3" md="2"><v-text-field v-model.number="config.averageLaps" type="number" min="1" label="Tururi valide consecutive"></v-text-field></v-col>
            <v-col cols="12" sm="9" md="10" class="d-flex flex-wrap align-center">
              <div class="d-flex align-center mr-5">
                <v-checkbox v-model="config.invalidateFlags.discontinuity" class="mr-1" label="Discontinuity ON/OFF"></v-checkbox>
                <v-tooltip top max-width="420">
                  <template v-slot:activator="{ on }"><v-icon small color="info" v-on="on">mdi-information-outline</v-icon></template>
                  <span>FLAG_DISCONTINUITY indicates that packet loss, high latency, or an unstable connection caused blinking or a lag spike and the server lost continuity of the car position. When enabled, laps carrying this flag are invalidated.</span>
                </v-tooltip>
              </div>
              <div class="d-flex align-center mr-5">
                <v-checkbox v-model="config.invalidateFlags.interpolatedCrossing" class="mr-1" label="Interpolated Crossing ON/OFF"></v-checkbox>
                <v-tooltip top max-width="420">
                  <template v-slot:activator="{ on }"><v-icon small color="info" v-on="on">mdi-information-outline</v-icon></template>
                  <span>FLAG_INTERPOLATED_CROSSING indicates that iRacing estimated a crossing of the start/finish line or a timing checkpoint while the car was blinking, lagging, or temporarily disconnected. When enabled, laps carrying this flag are invalidated.</span>
                </v-tooltip>
              </div>
              <div class="d-flex align-center">
                <v-checkbox v-model="config.invalidateFlags.clockSmash" class="mr-1" label="Clock Smash ON/OFF"></v-checkbox>
                <v-tooltip top max-width="420">
                  <template v-slot:activator="{ on }"><v-icon small color="info" v-on="on">mdi-information-outline</v-icon></template>
                  <span>FLAG_CLOCK_SMASH means the driver computer fell significantly behind in physics calculations and lost synchronization with the server, often causing a freeze, stutter, blink, teleport, or sudden position change. When enabled, laps carrying this flag are invalidated.</span>
                </v-tooltip>
              </div>
            </v-col>
          </v-row>
          <div class="d-flex flex-wrap align-center">
            <v-btn small color="primary" outlined class="mr-3 mb-2" :loading="busy" @click="saveSettings">Salvează regulile</v-btn>
            <v-chip small outlined color="primary" class="mr-2 mb-2">{{config.averageLaps}} tururi</v-chip>
            <v-chip small :color="config.invalidateFlags.discontinuity ? 'green' : 'grey'" :outlined="!config.invalidateFlags.discontinuity" class="mr-2 mb-2">Discontinuity: {{config.invalidateFlags.discontinuity ? 'ON' : 'OFF'}}</v-chip>
            <v-chip small :color="config.invalidateFlags.interpolatedCrossing ? 'green' : 'grey'" :outlined="!config.invalidateFlags.interpolatedCrossing" class="mr-2 mb-2">Interpolated crossing: {{config.invalidateFlags.interpolatedCrossing ? 'ON' : 'OFF'}}</v-chip>
            <v-chip small :color="config.invalidateFlags.clockSmash ? 'green' : 'grey'" :outlined="!config.invalidateFlags.clockSmash" class="mb-2">Clock smash: {{config.invalidateFlags.clockSmash ? 'ON' : 'OFF'}}</v-chip>
          </div>
          <v-divider class="my-4"></v-divider>
          <div class="font-weight-bold mb-2">Sesiuni configurate</div>
          <div v-if="!config.sessions.length" class="text--secondary mb-3">Nu există sesiuni iRacing configurate.</div>
          <v-chip v-for="session in config.sessions" :key="`${session.subsessionId}-${session.simSessionNumber}`" small class="mr-2 mb-2" :color="sessionColor(session.status)" :outlined="session.status !== 'imported'">
            <v-icon left small>{{sessionIcon(session.status)}}</v-icon>{{session.subsessionId}} / {{session.simSessionNumber}} ({{session.sessionName || 'secțiune necunoscută'}}) — {{sessionStatus(session.status)}}
          </v-chip>
          <v-row dense align="center" class="mt-2">
            <v-col cols="12" sm="5"><v-text-field v-model="subsessionId" label="iRacing subsession ID" hide-details></v-text-field></v-col>
            <v-col cols="12" sm="3"><v-btn color="primary" outlined :loading="busy" @click="inspectSession">Inspectează</v-btn></v-col>
          </v-row>
          <v-row v-if="inspection" dense align="center">
            <v-col cols="12" sm="6"><v-select v-model="selectedSessionNumber" :items="inspection.sections" item-value="simSessionNumber" :item-text="sectionLabel" label="Secțiunea de importat"></v-select></v-col>
            <v-col cols="12" sm="3"><v-btn color="primary" :disabled="selectedSessionConfigured" :loading="busy" @click="addSession">{{selectedSessionConfigured ? 'Deja configurată' : 'Adaugă la import'}}</v-btn></v-col>
          </v-row>
          <v-alert v-if="sessionsNeedingImport.length" type="warning" text dense class="mt-3 mb-0">
            {{sessionsNeedingImport.length}} {{sessionsNeedingImport.length === 1 ? 'sesiune configurată nu este inclusă' : 'sesiuni configurate nu sunt incluse'}} încă în rezultate. Apasă „Importă toate sesiunile”.
          </v-alert>
          <v-alert v-if="currentSummary" type="info" text dense class="mt-3 mb-0">
            Importate: {{lastSummary.imported}} / {{lastSummary.calculated}}. Utilizatori necunoscuți: {{lastSummary.unmatchedUsers.length}}. Mașini necunoscute: {{lastSummary.unmatchedCars.length}}.
            <div v-if="lastSummary.unmatchedUsers.length" class="mt-1">
              Piloți:
              <span v-for="driver in lastSummary.unmatchedUsers" :key="driver.driver" class="mr-2">
                {{driver.driverName || 'Nume indisponibil'}} ({{driver.driver}})
              </span>
            </div>
            <div v-if="lastSummary.unmatchedCars.length" class="mt-1">
              Mașini:
              <span v-for="car in lastSummary.unmatchedCars" :key="car.carModel" class="mr-2">
                {{car.carName || 'Model indisponibil'}} ({{car.carModel}})
              </span>
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn color="green darken-1" dark :disabled="!config.sessions.length" :loading="busy" @click="importSessions">Importă toate sesiunile</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="orange darken-2" :loading="busy" @click="recoverCache">Recuperează din JSON</v-btn>
        </v-card-actions>
          </div>
        </v-expand-transition>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import axios from '@/plugins/axios';

const emptyConfig = averageLaps => ({
  averageLaps: averageLaps || 1,
  invalidateFlags: {discontinuity: false, interpolatedCrossing: false, clockSmash: false},
  sessions: []
});

export default {
  props: {
    event: {type: Object, required: true},
    defaultAverageLaps: {type: Number, default: 1}
  },
  data () {
    return {
      busy: false,
      expanded: false,
      rolesLoaded: false,
      initializing: false,
      subsessionId: '',
      inspection: null,
      selectedSessionNumber: null,
      lastSummary: null,
      config: emptyConfig(this.defaultAverageLaps)
    };
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapGetters('user', ['userRoles']),
    isAdministrator () {
      return this.rolesLoaded && this.userRoles.some(role => role && role.name === 'Administrator');
    },
    isIRacingEvent () {
      const simulator = this.event && this.event.season && this.event.season.rules
        ? this.event.season.rules.simulator
        : null;
      return !!(simulator && (simulator.requiredDriverFields || []).includes('iRacingMemberId'));
    },
    sessionsNeedingImport () {
      return (this.config.sessions || []).filter(session => session.status !== 'imported' || session.importedRulesVersion !== this.config.rulesVersion);
    },
    currentSummary () {
      return this.sessionsNeedingImport.length ? null : this.lastSummary;
    },
    selectedSessionConfigured () {
      if (!this.inspection || this.selectedSessionNumber === null) return false;
      const subsessionId = Number(this.subsessionId);
      const simSessionNumber = Number(this.selectedSessionNumber);
      return (this.config.sessions || []).some(session => session.subsessionId === subsessionId && session.simSessionNumber === simSessionNumber);
    }
  },
  watch: {
    isIRacingEvent (value) {
      if (value) this.initialize();
    },
    isLoggedIn (value) {
      if (value) this.initialize();
    }
  },
  methods: {
    ...mapActions('user', ['getUserRoles']),
    ...mapActions('app', ['showSnackbar']),
    async initialize () {
      if (!this.isLoggedIn || !this.isIRacingEvent || this.initializing || this.rolesLoaded) return;
      this.initializing = true;
      try {
        await this.getUserRoles();
        this.rolesLoaded = true;
        if (this.isAdministrator) await this.loadConfig();
      } catch (error) {
        this.rolesLoaded = false;
      } finally {
        this.initializing = false;
      }
    },
    errorMessage (error) {
      return error && error.response && error.response.data && error.response.data.error
        ? error.response.data.error
        : 'Operațiunea iRacing nu a putut fi finalizată.';
    },
    notifyError (error) {
      this.showSnackbar({timeout: 7000, content: this.errorMessage(error), color: 'error', value: true});
    },
    async loadConfig () {
      const response = await axios.get(`/api/event/${this.event._id}/iracing-prequal`);
      this.config = Object.assign(emptyConfig(this.defaultAverageLaps), response.data.data || {});
      this.config.invalidateFlags = Object.assign(emptyConfig().invalidateFlags, this.config.invalidateFlags || {});
      this.config.sessions = this.config.sessions || [];
      this.lastSummary = this.config.lastImport && this.config.lastImport.summary || null;
    },
    async saveSettings () {
      this.busy = true;
      try {
        const response = await axios.post(`/api/event/${this.event._id}/iracing-prequal/settings`, {
          averageLaps: Number(this.config.averageLaps),
          invalidateFlags: this.config.invalidateFlags
        });
        this.config = response.data.data;
        this.showSnackbar({timeout: 4000, content: 'Regulile iRacing au fost salvate.', color: 'success', value: true});
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.busy = false;
      }
    },
    async inspectSession () {
      this.busy = true;
      this.inspection = null;
      try {
        const response = await axios.post(`/api/event/${this.event._id}/iracing-prequal/inspect`, {subsessionId: this.subsessionId});
        this.inspection = response.data.data;
        this.selectedSessionNumber = this.inspection.sections.length === 1 ? this.inspection.sections[0].simSessionNumber : null;
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.busy = false;
      }
    },
    async addSession () {
      if (this.selectedSessionNumber === null) return;
      this.busy = true;
      try {
        await axios.post(`/api/event/${this.event._id}/iracing-prequal/settings`, {
          averageLaps: Number(this.config.averageLaps), invalidateFlags: this.config.invalidateFlags
        });

        const response = await axios.post(`/api/event/${this.event._id}/iracing-prequal/sessions`, {
          subsessionId: Number(this.subsessionId),
          simSessionNumber: Number(this.selectedSessionNumber)
        });
        this.config = response.data.data;
        this.inspection = null;
        this.subsessionId = '';
        this.lastSummary = null;
        this.showSnackbar({timeout: 6000, content: 'Sesiunea a fost configurată, dar rezultatele nu au fost încă importate. Apasă „Importă toate sesiunile”.', color: 'success', value: true});
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.busy = false;
      }
    },
    async importSessions () {
      this.busy = true;
      try {
        await axios.post(`/api/event/${this.event._id}/iracing-prequal/settings`, {
          averageLaps: Number(this.config.averageLaps), invalidateFlags: this.config.invalidateFlags
        });

        const response = await axios.post(`/api/event/${this.event._id}/iracing-prequal/import`);
        this.lastSummary = response.data.data;
        await this.loadConfig();
        this.$emit('imported');
        this.showSnackbar({timeout: 5000, content: 'Rezultatele iRacing au fost importate.', color: 'success', value: true});
      } catch (error) {
        await this.loadConfig().catch(() => {});
        this.notifyError(error);
      } finally {
        this.busy = false;
      }
    },
    async recoverCache () {
      this.busy = true;
      try {
        const preview = (await axios.get(`/api/event/${this.event._id}/iracing-prequal/recovery`)).data.data;
        const sessions = preview.sourceSessions.map(session => `${session.subsessionId}/${session.simSessionNumber}`).join(', ');
        if (!window.confirm(`Recuperezi ${preview.importable} rezultate din copia ${preview.createdAt}? Sesiuni: ${sessions || 'nespecificate'}`)) return;
        const response = await axios.post(`/api/event/${this.event._id}/iracing-prequal/recovery`);
        this.lastSummary = response.data.data;
        await this.loadConfig();
        this.$emit('imported');
        this.showSnackbar({timeout: 5000, content: 'Rezultatele au fost recuperate din JSON.', color: 'success', value: true});
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.busy = false;
      }
    },
    sectionLabel (section) {
      return `${section.simSessionNumber}: ${section.sessionName} (${section.resultCount} rezultate)`;
    },
    sessionStatus (status) {
      return {pending: 'neimportată', importing: 'se importă', imported: 'importată', failed: 'eșuată', outdated: 'reguli modificate'}[status] || status;
    },
    sessionColor (status) {
      return {imported: 'green', failed: 'red', outdated: 'orange', importing: 'blue'}[status] || 'grey';
    },
    sessionIcon (status) {
      return {imported: 'mdi-check', failed: 'mdi-alert', outdated: 'mdi-refresh', importing: 'mdi-progress-download'}[status] || 'mdi-clock-outline';
    }
  },
  mounted () {
    this.initialize();
  }
};
</script>
